import { useAtomValue } from "jotai";
import camelCase from "lodash/camelCase";
import first from "lodash/first";
import { useRouter } from "next/router";
import qs from "qs";
import { useCallback, useEffect } from "react";
import { FollowAction, isFollowType } from "scmp-sdk";
import type { DeepNonNullable } from "utility-types";

import { accountAtom } from "shared/lib/account";
import { useRouterReplaceWithSanitizedKeys } from "shared/lib/router";
import { notEmpty, parseArray } from "shared/lib/utils";

import type { FollowQueryStringPayload } from "scmp-app/components/follow-button/types";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { useAcquisitionConfig } from "scmp-app/lib/app/hooks";
import { useUserService } from "scmp-app/lib/user-service/hooks";

import { SanitizedUrlKeys } from "./consts";

export const useFollowConfigActionRedirect = () => {
  const { acquisitionConfigState } = useAcquisitionConfig();
  const { followAction } = useUserService();
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();

  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);

  const execute = useCallback(
    (
      currentLocation: string,
      followConfig: DeepNonNullable<FollowQueryStringPayload>["action"]["follow_config"],
      utmSource?: DeepNonNullable<FollowQueryStringPayload>["utm_source"],
    ) => {
      const acquisitionConfig = acquisitionConfigState.result;
      if (!acquisitionConfig || acquisitionConfigState.status !== "success") return;

      const sanitizedFollowConfig = Object.entries(followConfig)
        .map(([type, configKeys]) => {
          const parsedConfigKeys = parseArray(configKeys);
          const sanitizedParsedConfigKeys = parsedConfigKeys.filter(
            configKey => acquisitionConfig[camelCase(configKey)]?.list?.length > 0,
          );
          if (!isFollowType(type) || sanitizedParsedConfigKeys.length === 0) return null;
          return {
            configKeys: sanitizedParsedConfigKeys,
            type,
          };
        })
        .filter(notEmpty);

      sanitizedFollowConfig.forEach(({ configKeys, type }, followConfigIndex) => {
        if (!user) {
          const configKey: string = camelCase(first(configKeys)) ?? "";
          const isFromAmp = !!utmSource;
          openLoginDialog({
            description: isFromAmp
              ? `Please log in or register in order to follow ${acquisitionConfig[configKey].name} and be notified when new content is published.`
              : "",
            destination: currentLocation,
            ga4CustomParameter: {
              follow_name: acquisitionConfig[configKey].name,
              follow_type: "topic",
              trigger_point: "follow",
            },
            title: isFromAmp ? "One Last Step" : "Sign In / Register",
          });
          return;
        }

        configKeys.forEach(async (configKey, keyIndex) => {
          const config = acquisitionConfig[camelCase(configKey)];

          await followAction(
            FollowAction.Follow,
            type,
            config.list.map(({ id }) => id),
            () => {
              if (
                followConfigIndex === sanitizedFollowConfig.length - 1 &&
                keyIndex === configKeys.length - 1
              ) {
                void replace(
                  SanitizedUrlKeys,
                  { pathname, query },
                  {
                    shallow: true,
                  },
                );
              }
            },
          );
        });
      });
    },
    [
      acquisitionConfigState.result,
      acquisitionConfigState.status,
      user,
      openLoginDialog,
      followAction,
      replace,
      pathname,
      query,
    ],
  );

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as FollowQueryStringPayload;
    if (!parsedQueryString.action?.follow_config) return;

    void execute(
      window.location.href,
      parsedQueryString.action.follow_config,
      parsedQueryString.utm_source,
    );
  }, [execute, pathname, query, replace]);

  return null;
};
